import { useCallback, useEffect, useRef } from "react";
import useDebounceCallback from "./useDebounceCallback";

type useResizeProps = {
  minWidth: number | string;
  maxWidth: number | string;
  width: number;
  setWidth: (width: number) => void;
};

type useResizeReturn = {
  width: number;
  enableResize: () => void;
};

/**
 * useResize
 *
 * A custom React hook that enables horizontal resizing based on mouse movement.
 * It tracks mouse dragging using document-level mouse events and updates the
 * width according to the mouse's X position (clientX), constrained between
 * the provided min and max values.
 *
 * - `enableResize` should be called on mouse down to start resizing
 * - Resizing continues while the mouse moves
 * - Resizing stops automatically on mouse up
 *
 * @param minWidth - Minimum allowed width
 * @param maxWidth - Maximum allowed width
 * @param width - Current width value
 * @param setWidth - State setter to update the width
 *
 * @returns An object containing the current width and a function to enable resizing
 */

export const getPxValue = (value: string | number): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string" && value.endsWith("%")) {
    const percentInNumber = parseFloat(value);
    return (window.innerWidth * percentInNumber) / 100;
  }
  throw new Error("Invalid width value, must be px number or % string");
};

const useResize = ({ minWidth, maxWidth, width, setWidth }: useResizeProps): useResizeReturn => {
  const isResizing = useRef(false);
  // Debounced setter for width using shared hook
  const debouncedSetWidth = useDebounceCallback(setWidth, 10); // changed on every 10 ms

  const enableResize = useCallback(() => {
    isResizing.current = true;
  }, []);

  const disableResize = useCallback(() => {
    isResizing.current = false;
  }, []);

  const resize = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (isResizing.current) {
        if (e instanceof TouchEvent) {
          // Prevent scrolling while resizing on touch devices
          if (e.cancelable) e.preventDefault();
        }

        const newWidth = e instanceof TouchEvent ? e?.changedTouches?.item(0)?.clientX! : e.clientX; // X position from left

        if (newWidth >= getPxValue(minWidth) && newWidth <= getPxValue(maxWidth)) {
          // Use debounced setter to limit updates
          debouncedSetWidth(newWidth);
        }
      }
    },
    [isResizing, minWidth, maxWidth, debouncedSetWidth]
  );

  useEffect(() => {
    document.addEventListener("mousemove", resize);
    document.addEventListener("mouseup", disableResize);

    // Passive: false is required to allow preventDefault() in touchmove
    document.addEventListener("touchmove", resize, { passive: false });
    document.addEventListener("touchend", disableResize);
    document.addEventListener("touchcancel", disableResize);

    return () => {
      document.removeEventListener("mousemove", resize);
      document.removeEventListener("mouseup", disableResize);

      document.removeEventListener("touchmove", resize);
      document.removeEventListener("touchend", disableResize);
      document.removeEventListener("touchcancel", disableResize);
      // Cancel any pending debounced call
      if (debouncedSetWidth && typeof debouncedSetWidth.cancel === "function") {
        debouncedSetWidth.cancel();
      }
    };
  }, [debouncedSetWidth, disableResize, resize]);

  return { width, enableResize };
};

export default useResize;
